import { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconAI } from '@components/icons/ai';
import { IconDropRight } from '@components/icons/dropright';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { uuid } from '@utils/uuid';

import { ReviewFinding, ReviewTargetKind } from 'api/journey-reviews/requests';

import { FindingTargetIcon } from './finding-target-icon';
import { FindingTargetLabel } from './finding-target-label';
import { NavigatorFixState, severityColor } from './review-utils';
import { useHighlightedFindingStore } from './use-highlighted-finding-store';

import { JourneyNode } from '../node-types/types';

export type FindingCardProps = {
  finding: ReviewFinding;
  node?: JourneyNode;
  onJumpTo: (finding: ReviewFinding) => void;
  // Exactly one of these is provided: onIgnore for open findings, onRestore for
  // the ignored tab.
  onIgnore?: (finding: ReviewFinding) => void;
  onRestore?: (finding: ReviewFinding) => void;
  onFixWithNavigator?: (finding: ReviewFinding) => void;
  navigatorFixState?: NavigatorFixState;
};

const OPEN_LABEL_KEY: Record<ReviewTargetKind, keyof (typeof locale)['en']> = {
  action: 'openAction',
  trigger: 'openTrigger',
  branch: 'showBranch',
  field: 'openField',
};

const FIX_DISABLED_REASON_KEY: Partial<Record<NavigatorFixState, keyof (typeof locale)['en']>> = {
  busy: 'navigatorBusy',
  loading: 'navigatorLoading',
};

export const FindingCard = ({
  finding,
  node,
  onJumpTo,
  onIgnore,
  onRestore,
  onFixWithNavigator,
  navigatorFixState = 'available',
}: FindingCardProps) => {
  const { t } = useScopedTranslate(locale);
  const [isExpanded, setIsExpanded] = useState(false);
  const summaryButtonRef = useRef<HTMLButtonElement>(null);
  const [descriptionId] = useState(() => `finding-description-${uuid()}`);
  const [targetId] = useState(() => `finding-target-${uuid()}`);
  const [fixReasonId] = useState(() => `finding-fix-reason-${uuid()}`);
  const [descriptionRef, isDescriptionClamped] = useIsOverflowing('vertical');
  const highlightFinding = useHighlightedFindingStore((state) => state.highlightFinding);
  const clearFinding = useHighlightedFindingStore((state) => state.clearFinding);

  // Ignoring the card while the pointer is on it would otherwise leave the
  // node outlined with nothing hovered.
  useEffect(() => () => clearFinding(finding.target), [clearFinding, finding.target]);

  // `isExpanded` first: once open the clamp is gone, so the measurement reports
  // "fits" and the control would disappear under the pointer.
  const hasSuggestedFix = Boolean(finding.suggestedFix);
  const fixDisabledReason = FIX_DISABLED_REASON_KEY[navigatorFixState];
  const ignoredTime = finding.ignoredAt
    ? abbreviateDate(finding.ignoredAt, 'en', 'weekday-datetime')
    : '';
  const canExpand = isExpanded || isDescriptionClamped || hasSuggestedFix;

  return (
    <Card
      interactive
      space={3}
      dataTestId="finding-card"
      onClick={(event) => {
        const selection = window.getSelection();
        const isEndingSelectionHere =
          event.detail !== 0 &&
          Boolean(selection && !selection.isCollapsed) &&
          event.currentTarget.contains(selection?.anchorNode ?? null);
        if (isEndingSelectionHere) return;
        onJumpTo(finding);
      }}
      onMouseEnter={() => highlightFinding(finding.target, severityColor[finding.severity])}
      onMouseLeave={() => clearFinding(finding.target)}
    >
      <Stack orientation="horizontal" space={2} align="flex-start">
        <SummaryButton
          ref={summaryButtonRef}
          type="button"
          aria-label={t('summaryLabel', {
            action: t(OPEN_LABEL_KEY[finding.target.kind]),
            title: finding.title,
          })}
          aria-describedby={`${targetId} ${descriptionId}`}
        >
          <FindingTargetIcon target={finding.target} node={node} />
          <SummaryText>
            <Text font="semibold14" text={finding.title} />
            <div id={targetId}>
              <FindingTargetLabel target={finding.target} />
            </div>
            <Description id={descriptionId} ref={descriptionRef} $isClamped={!isExpanded}>
              <Text font="regular14" color="textSecondary" text={finding.description} />
            </Description>
          </SummaryText>
        </SummaryButton>
        {canExpand && (
          <IconButton
            variant="ghost-purple"
            size="sm"
            icon={<IconDropRight color="textHelper" />}
            isRotated={isExpanded}
            stopPropagation
            aria-expanded={isExpanded}
            aria-label={isExpanded ? t('hideDetails') : t('showDetails')}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          />
        )}
      </Stack>

      <Collapse isCollapsed={!isExpanded || !hasSuggestedFix} overflowType="hidden">
        <SuggestedFix>
          <Text font="regular14">
            <FixLabel>{t('fixLabel')}</FixLabel> {finding.suggestedFix}
          </Text>
        </SuggestedFix>
      </Collapse>

      <Actions onClick={(event) => event.stopPropagation()}>
        {onFixWithNavigator && (
          <LeadingAction>
            <Button
              variant="secondary"
              size="xs"
              iconLeft={<IconAI color="aiGradientTeal100" />}
              text={navigatorFixState === 'sent' ? t('sentToNavigator') : t('fixWithNavigator')}
              isDisabled={navigatorFixState !== 'available'}
              tooltip={navigatorFixState === 'busy' ? t('navigatorBusy') : undefined}
              aria-describedby={fixDisabledReason ? fixReasonId : undefined}
              onClick={() => {
                summaryButtonRef.current?.focus();
                onFixWithNavigator(finding);
              }}
              dataTestid="fix-with-navigator"
            />
            {fixDisabledReason && (
              <VisuallyHidden id={fixReasonId}>{t(fixDisabledReason)}</VisuallyHidden>
            )}
          </LeadingAction>
        )}
        {finding.ignored && finding.ignoredAt && (
          <IgnoredBy>
            {finding.ignoredBy && (
              <Avatar
                name={finding.ignoredBy.name}
                src={finding.ignoredBy.picture}
                size="sm"
                color="purple"
              />
            )}
            <Text
              font="regular12"
              color="textHelper"
              ellipsisWithTooltip
              text={
                finding.ignoredBy
                  ? t('ignoredBy', { name: finding.ignoredBy.name, time: ignoredTime })
                  : t('ignoredAt', { time: ignoredTime })
              }
            />
          </IgnoredBy>
        )}
        {onRestore ? (
          <Button variant="ghost" size="xs" text={t('reopen')} onClick={() => onRestore(finding)} />
        ) : onIgnore ? (
          <Button variant="ghost" size="xs" text={t('ignore')} onClick={() => onIgnore(finding)} />
        ) : null}
      </Actions>
    </Card>
  );
};

const SummaryButton = styled.button({
  display: 'flex',
  alignItems: 'flex-start',
  gap: pxToRem(8),
  flex: 1,
  minWidth: 0,
  textAlign: 'left',
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'inherit',
  cursor: 'pointer',
});

const SummaryText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(2),
  flex: 1,
  minWidth: 0,
});

const Description = styled.div<{ $isClamped: boolean }>((props) => ({
  marginTop: pxToRem(2),
  ...(props.$isClamped && {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  }),
}));

const SuggestedFix = styled.div({
  marginTop: pxToRem(8),
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: pxToRem(4),
  background: theme.gradient.aiSurface10,
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  cursor: 'default',
  gap: pxToRem(4),
  marginTop: pxToRem(10),
  paddingTop: pxToRem(10),
  borderTop: `1px solid ${theme.color.border10}`,
});

const LeadingAction = styled.div({
  marginRight: 'auto',
});

const IgnoredBy = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(4),
  minWidth: 0,
  marginRight: 'auto',
});

const FixLabel = styled.span({
  font: theme.font.semibold14,
});

export const locale = {
  en: {
    openAction: 'Open action',
    openTrigger: 'Open trigger',
    showBranch: 'Show branch',
    openField: 'Open field',
    ignore: 'Ignore',
    reopen: 'Reopen',
    fixLabel: 'Fix:',
    fixWithNavigator: 'Fix with Navigator',
    sentToNavigator: 'Sent to Navigator',
    navigatorBusy: 'The Navigator is busy with another request',
    navigatorLoading: 'Checking whether the Navigator is free…',
    summaryLabel: '{action}: {title}',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    ignoredBy: 'Ignored by {name} · {time}',
    ignoredAt: 'Ignored {time}',
  },
};
